import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Radio, Divider, Spin } from 'antd';
import { CalendarPicker, NoticeBar } from 'antd-mobile';
import {
  CalendarOutline,
  DownFill,
  ExclamationTriangleOutline,
} from 'antd-mobile-icons';
import caricon from '../../images/car-icon.svg';
import dayjs from 'dayjs';

import Header from './components/Header';
import InputField from './components/InputField';
import CustomPicker from './components/CustomPicker';
import ModalPopup from './components/ModalPopup';
import ResultModal from './components/ResultModal';
import ErrorModal from './components/ErrorModal';
import TimeSlotSelector from './components/TimeSlotSelector';

import { AuthValid } from 'shared/httpHandler';
import axios from 'axios';

import './booking.scss';

const BookingMaintenance = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'LEXUS Line OA | 預約保養';
  }, []);

  const [token, setToken] = useState('');
  const [lineId, setLineId] = useState('');
  const [carNo, setCarNo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [allowConfirmError, setAllowConfirmError] = useState(false);
  const tokenExpiredMessage = (
    <>
      <b>此頁面已失效</b>
      <br />
      請關閉目前頁面並重新點選
      <br />
      <b>車主專區 {'>'} 預約服務</b>
    </>
  );
  const fetchFailedMessage = <>伺服器忙線中, 請稍後再試</>;

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get('token');

    if (token) {
      try {
        const payload = AuthValid.parseJwt(token);
        setToken(token);
        setLineId(payload.sub);
        setCarNo(payload.carNo);
      } catch (error) {
        console.error('Failed to parse JWT token:', error);
        setIsError(true);
        setErrorMessage(tokenExpiredMessage);
        setAllowConfirmError(false);
      }
    } else {
      navigate('/Error404');
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    region: '',
    serviceType: '',
    contactTime: '',
    description: '',
    isWaiting: 'undefined', // 是否在廠等候
    needsQuickService: 'undefined', // 重置快速定保服務選項
    needsPickup: 'no', // 是否需要取送車服務（取送車服務暫停，預設 no）
    pickupAddress: '', // 取送車地址
    pickupTime: '', // 取送車時段
  });

  const [selectedPeriod, setSelectedPeriod] = useState('morning'); // 'morning' or 'afternoon'
  const [showCalendar, setShowCalendar] = useState(false);

  // 限制 CalendarPicker 只顯示當月及下個月
  const today = dayjs();
  const currentMonthStart = today.startOf('month');
  const nextMonthEnd = today.add(1, 'month').endOf('month');

  // 控制三個 Popup
  const [regionPickerVisible, setRegionPickerVisible] = useState(false);
  const [servicePickerVisible, setServicePickerVisible] = useState(false);
  const [contactPickerVisible, setContactPickerVisible] = useState(false);

  // 服務廠選項
  const [regionOptions, setRegionOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState({});

  // const regionOptions = [
  //   { label: '台北市', value: 'taipei' },
  //   { label: '新北市', value: 'new-taipei' },
  // ];

  // const serviceOptions = {
  //   taipei: [
  //     { label: 'LEXUS南港廠', value: 'taipei-nangang', phone: '02-12345678' },
  //     { label: 'LEXUS內湖廠', value: 'taipei-neihu', phone: '02-87654321' },
  //   ],
  //   'new-taipei': [
  //     {
  //       label: 'LEXUS淡水廠',
  //       value: 'new-taipei-banciao',
  //       phone: '02-99999999',
  //     },
  //     {
  //       label: 'LEXUS永和廠',
  //       value: 'new-taipei-xinzhuang',
  //       phone: '02-88888888',
  //     },
  //   ],
  // };

  const selectedService = serviceOptions[formData.region]?.find(
    (s) => s.value === formData.serviceType
  );

  const phoneNumber = selectedService?.phone || 'xx-xxxxxxxx';
  const factoryLabel = selectedService?.label || 'oo服務廠';

  const getServiceInfo = () => {
    setIsLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_BASE_URL}/booking/service_info?car_no=${carNo}&auth_token=${token}`
      )
      .then((res) => {
        if (res.status === 200) {
          const resData = res.data;
          if (resData.success) {
            const distinctCities = [
              ...new Set(resData.data.map((item) => item.CITY)),
            ];
            setRegionOptions(
              distinctCities.map((city) => ({
                label: city,
                value: city,
              }))
            );
            const serviceOptionsByCity = {};
            distinctCities.forEach((city) => {
              serviceOptionsByCity[city] = resData.data
                .filter((item) => item.CITY === city)
                .map((item) => ({
                  label: item.BRNHNM,
                  value: `${item.DLRCD}-${item.BRNHCD}`,
                  dealerCode: item.DLRCD,
                  branchCode: item.BRNHCD,
                  phone: item.TEL || '',
                }));
            });
            setServiceOptions(serviceOptionsByCity);
          } else {
            if (resData.error === 'LexusAuthError') {
              setIsError(true);
              setErrorMessage(tokenExpiredMessage);
              setAllowConfirmError(false);
            } else {
              throw new Error(`${resData.error} ${resData.message}`);
            }
          }
        } else {
          throw new Error(`${res.status} ${res.statusText}`);
        }
      })
      .catch((err) => {
        console.error('err', err);
        setIsError(true);
        setErrorMessage(fetchFailedMessage);
        setAllowConfirmError(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (token && carNo) {
      getServiceInfo();
    }
  }, [token, carNo]);

  // 預約時段選項
  const [availableTime, setAvailableTime] = useState({});

  const getAvailableTime = (dealerCode, branchCode) => {
    setIsLoading(true);
    axios
      .get(
        `${process.env.REACT_APP_BASE_URL}/booking/available_time?dealer_code=${dealerCode}&branch_code=${branchCode}&auth_token=${token}`
      )
      .then((res) => {
        if (res.status === 200) {
          const resData = res.data;
          if (resData.success) {
            const timeByDate = {};
            if (resData.data && Array.isArray(resData.data)) {
              resData.data.forEach((item) => {
                const { ORDERDT, WEEK_DAY, TIME_SET } = item;
                if (!timeByDate[ORDERDT]) {
                  timeByDate[ORDERDT] = {
                    weekDay: WEEK_DAY,
                    availableTimes: [],
                  };
                }
                if (
                  TIME_SET &&
                  !timeByDate[ORDERDT].availableTimes.includes(TIME_SET)
                ) {
                  timeByDate[ORDERDT].availableTimes.push(TIME_SET);
                }
              });
            }
            setAvailableTime(timeByDate);
          } else {
            if (resData.error === 'LexusAuthError') {
              setIsError(true);
              setErrorMessage(tokenExpiredMessage);
              setAllowConfirmError(false);
            } else {
              throw new Error(`${resData.error} ${resData.message}`);
            }
          }
        } else {
          throw new Error(`${res.status} ${res.statusText}`);
        }
      })
      .catch((err) => {
        console.error('err', err);
        setIsError(true);
        setErrorMessage(fetchFailedMessage);
        setAllowConfirmError(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const contactOptions = [
    { label: '8:00-12:00', value: '1' },
    { label: '13:00-17:30', value: '2' },
    { label: '18:00-20:30', value: '3' },
    { label: '任何時段(8:00-20:30)', value: '4' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 三個 Picker 的 onConfirm
  const handleRegionConfirm = (val) => {
    setFormData((prev) => ({
      ...prev,
      region: val,
      serviceType: '', // 重置服務廠
    }));
  };

  const pickerStyle = {
    '--adm-font-size-main': '16px',
    '--item-height': '50px',
  };

  // 取送車時間計算函數
  const calculatePickupTime = (appointmentTime) => {
    if (!appointmentTime) return '';

    // 解析時間
    const [hours, minutes] = appointmentTime.split(':').map(Number);

    // 計算30分鐘前的時間
    let pickupHour = hours;
    let pickupMinute = minutes - 30;

    if (pickupMinute < 0) {
      pickupHour -= 1;
      pickupMinute += 60;
    }

    // 格式化時間
    const startTime = `${String(pickupHour).padStart(2, '0')}:${String(
      pickupMinute
    ).padStart(2, '0')}`;
    return `${startTime}~${appointmentTime}`;
  };

  // 當「預約時段」「預約日期」變更時，更新「取送車時段」
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      pickupTime: calculatePickupTime(prev.preferredTime),
    }));

    // 取送車服務暫停，停用取送車時段檢查
    // checkPickupTime();
    checkQuickServiceTime();
  }, [formData.preferredTime, formData.preferredDate, formData.isWaiting]); // 監聽預約時段的變化

  // 預約取送車時段 是否顯示警告
  const [isPickupTimeWarning, setIsPickupTimeWarning] = useState(false);
  const [isPickupTimeLoading, setIsPickupTimeLoading] = useState(false);

  // 快速定保時段 是否顯示警告
  const [isQuickServiceTimeWarning, setQuickServiceTimeWarning] =
    useState(false);
  const [isQuickServiceTimeLoading, setIsQuickServiceTimeLoading] =
    useState(false);

  const checkPickupTime = () => {
    const dealerCode = formData.serviceType.split('-')[0];
    const branchCode = formData.serviceType.split('-')[1];
    const preferredDate = formData.preferredDate;
    const pickupTime = formData.pickupTime.split('~')[0];

    if (dealerCode && branchCode && preferredDate && pickupTime) {
      // setIsPickupTimeWarning(false);
      setIsPickupTimeLoading(true);
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/booking/check_pickup_time?dealer_code=${dealerCode}&branch_code=${branchCode}&car_no=${carNo}&preferred_date=${preferredDate}&pickup_time=${pickupTime}&auth_token=${token}`
        )
        .then((res) => {
          if (res.status === 200) {
            const resData = res.data;
            if (resData.success) {
              const isValid = resData.data.is_valid;
              if (!isValid) {
                setIsPickupTimeWarning(true);
              } else {
                setIsPickupTimeWarning(false);
              }
            } else {
              throw new Error(`${resData.error} ${resData.message}`);
            }
          } else {
            throw new Error(`${res.status} ${res.statusText}`);
          }
        })
        .catch((err) => {
          console.error('err', err);
        })
        .finally(() => {
          setIsPickupTimeLoading(false);
        });
    } else {
      // 每給足條件, 亮警示
      setIsPickupTimeWarning(true);
    }
  };

  const checkQuickServiceTime = () => {
    const dealerCode = formData.serviceType.split('-')[0];
    const branchCode = formData.serviceType.split('-')[1];
    const preferredDate = formData.preferredDate;
    const quickServiceTime = formData.preferredTime;

    if (dealerCode && branchCode && preferredDate && quickServiceTime) {
      // setQuickServiceTimeWarning(false);
      axios
        .get(
          `${process.env.REACT_APP_BASE_URL}/booking/check_service_time?dealer_code=${dealerCode}&branch_code=${branchCode}&car_no=${carNo}&preferred_date=${preferredDate}&service_time=${quickServiceTime}&auth_token=${token}`
        )
        .then((res) => {
          if (res.status === 200) {
            const resData = res.data;
            if (resData.success) {
              const isValid = resData.data.is_valid;
              if (!isValid) {
                setQuickServiceTimeWarning(true);
              } else {
                setQuickServiceTimeWarning(false);
              }
            } else {
              setFormData((prev) => ({ ...prev, needsQuickService: 'no' }));
              throw new Error(`${resData.error} ${resData.message}`);
            }
          } else {
            throw new Error(`${res.status} ${res.statusText}`);
          }
        })
        .catch((err) => {
          console.error('err', err);
        })
        .finally(() => {
          setIsQuickServiceTimeLoading(false);
        });
    } else {
      // 條件不足的時候, 亮警示
      setQuickServiceTimeWarning(true);
    }
  };

  // 監聽取送車時段變化（取送車服務暫停，停用）
  // useEffect(() => {
  //   if (formData.serviceType && formData.preferredDate && formData.pickupTime) {
  //     checkPickupTime();
  //   }
  // }, [
  //   formData.serviceType,
  //   formData.isWaiting,
  //   formData.preferredDate,
  //   formData.pickupTime,
  // ]);

  useEffect(() => {
    if (isPickupTimeWarning) {
      setFormData((prev) => ({
        ...prev,
        needsPickup: 'no',
        pickupTime: '',
      }));
    }
  }, [isPickupTimeWarning]);

  const handleWaitingChange = (e) => {
    const isWaiting = e.target.value;
    setFormData((prev) => ({
      ...prev,
      isWaiting,
      needsPickup: 'no', // 取送車服務暫停，固定為 no
      pickupAddress: isWaiting === 'no' ? prev.pickupAddress : '', // 若選擇「是」，清空地址
      pickupTime: isWaiting === 'no' ? prev.pickupTime : '', // 若選擇「是」，清空取送車時段
    }));
  };

  // 處理是否選擇雙人快速定保服務
  const handleQuickServiceChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      needsQuickService: e.target.value,
    }));

    // 查詢快速定保可用時段
    if (e.target.value === 'yes') {
      checkQuickServiceTime();
    }
  };

  const [errors, setErrors] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [isResultModalVisible, setIsResultModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'success' or 'fail'

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = '請輸入預約人姓名';
    if (!formData.phone.trim()) newErrors.phone = '請輸入預約人手機號碼';
    if (!formData.region) newErrors.region = '請選擇地區';
    if (!formData.serviceType) newErrors.serviceType = '請選擇服務廠';
    if (!formData.preferredDate) newErrors.preferredDate = '請選擇預約日期';
    if (!formData.preferredTime) newErrors.preferredTime = '請選擇預約時段';
    if (formData.isWaiting === 'undefined')
      newErrors.isWaiting = '請選擇是否在廠等候';
    // 取送車服務暫停，關閉相關檢查
    // if (formData.isWaiting === 'no' && formData.needsPickup === 'undefined')
    //   newErrors.needsPickup = '請選擇是否預約取送車服務';
    // if (formData.needsPickup === 'yes' && !formData.pickupAddress.trim())
    //   newErrors.pickupAddress = '請輸入預約取送車地址';
    if (!formData.contactTime) newErrors.contactTime = '請選擇電話聯絡時段';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // 顯示失敗 Modal
      setModalType('fail');
      setIsResultModalVisible(true);
    } else {
      setErrors({});
      setIsLoading(true);
      axios
        .post(
          `${process.env.REACT_APP_BASE_URL}/booking/maintenance?car_no=${carNo}&auth_token=${token}`,
          {
            ...formData,
            // 取送車服務暫停，API 仍需欄位，固定送預設值
            needsPickup: 'no',
            pickupAddress: '',
            pickupTime: '',
          }
        )
        .then((res) => {
          if (res.status === 200) {
            const resData = res.data;
            if (resData.success) {
              // 顯示成功 Modal
              setModalType('success');
              setSubmitDisabled(true);
              setIsResultModalVisible(true);
            } else {
              if (resData.error === 'LexusAuthError') {
                setIsError(true);
                setErrorMessage(tokenExpiredMessage);
                setAllowConfirmError(false);
              } else {
                setIsError(true);
                setErrorMessage(resData.message);
                setAllowConfirmError(true);
                throw new Error(`${resData.error} ${resData.message}`);
              }
            }
          } else {
            throw new Error(`${res.status} ${res.statusText}`);
          }
        })
        .catch((err) => {
          console.error('err', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <div className="booking-container">
      {isLoading && <Spin fullscreen />}
      <ErrorModal
        isVisible={isError}
        message={errorMessage}
        allowConfirm={allowConfirmError}
        onClose={() => {
          setIsError(false);
          setErrorMessage('');
          setAllowConfirmError(false);
        }}
      />
      <Header />
      <div className="form-container">
        <div className="form-section">
          <form onSubmit={handleSubmit}>
            <div className="form-content">
              <Flex vertical gap={4}>
                <Flex align="center" gap="small">
                  <img src={caricon} alt="Car icon" />
                  <div className="form-title">預約保養</div>
                </Flex>
                <div className="form-subtitle">
                  <span className="label">車號：</span>
                  <span className="value">{carNo}</span>
                </div>
              </Flex>
              <Divider />
              <InputField
                label="預約人姓名"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
              />
              <InputField
                label="預約人手機號碼"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                type="tel"
              />
              <div className="form-group">
                <label>
                  預約服務廠
                  {errors.serviceType && (
                    <span className="error-text">{errors.serviceType}</span>
                  )}
                </label>
                <Flex gap="middle">
                  <div
                    className="picker-trigger region-picker"
                    onClick={() => setRegionPickerVisible(true)}>
                    <span
                      className={`picker-placeholder ${
                        formData.region ? 'has-value' : ''
                      }`}>
                      {formData.region
                        ? regionOptions.find((r) => r.value === formData.region)
                            ?.label
                        : '地區'}
                    </span>
                    <DownFill className="down-icon" />
                    <CustomPicker
                      visible={regionPickerVisible}
                      onClose={() => setRegionPickerVisible(false)}
                      onConfirm={(val) => handleRegionConfirm(val)}
                      title="選擇地區"
                      columns={regionOptions}
                      defaultValue={formData.region}
                    />
                  </div>
                  <div
                    className={`picker-trigger service-picker ${
                      !formData.region ? 'disabled' : ''
                    }`}
                    onClick={() => {
                      if (formData.region) setServicePickerVisible(true);
                    }}>
                    <span
                      className={`picker-placeholder ${
                        formData.serviceType ? 'has-value' : ''
                      }`}>
                      {formData.serviceType
                        ? serviceOptions[formData.region]?.find(
                            (s) => s.value === formData.serviceType
                          )?.label
                        : '服務廠'}
                    </span>
                    <DownFill className="down-icon" />
                    <CustomPicker
                      visible={servicePickerVisible}
                      onClose={() => setServicePickerVisible(false)}
                      onConfirm={(val) => {
                        setFormData((prev) => ({ ...prev, serviceType: val }));
                        getAvailableTime(val.split('-')[0], val.split('-')[1]);
                      }}
                      title="選擇服務廠"
                      columns={serviceOptions[formData.region] || []}
                      defaultValue={formData.serviceType}
                    />
                  </div>
                </Flex>
              </div>

              <div className="form-group">
                <label>
                  預約日期
                  {errors.preferredDate && (
                    <span className="error-text">{errors.preferredDate}</span>
                  )}
                </label>
                {/* 預約日期 */}
                <div
                  className="picker-trigger"
                  onClick={() => setShowCalendar(true)}>
                  <span
                    className={`picker-placeholder ${
                      formData.preferredDate ? 'has-value' : ''
                    }`}>
                    {formData.preferredDate || '請選擇日期'}
                  </span>
                  <CalendarOutline className="calendar-icon" />
                </div>
                <CalendarPicker
                  visible={showCalendar}
                  onClose={() => setShowCalendar(false)}
                  style={pickerStyle}
                  title={<div style={{ fontWeight: 'bold' }}>選擇預約日期</div>}
                  confirmText="確定"
                  onConfirm={(val) => {
                    const year = val.getFullYear();
                    const month = String(val.getMonth() + 1).padStart(2, '0');
                    const day = String(val.getDate()).padStart(2, '0');
                    const formattedDate = `${year}/${month}/${day}`;
                    setFormData((prev) => ({
                      ...prev,
                      preferredDate: formattedDate,
                    }));
                  }}
                  min={currentMonthStart}
                  max={nextMonthEnd}
                  shouldDisableDate={(date) =>
                    !availableTime[dayjs(date).format('YYYY/MM/DD')]
                  }
                />
              </div>

              <div className="form-group">
                <label>
                  預約時段
                  {errors.preferredTime && (
                    <span className="error-text">{errors.preferredTime}</span>
                  )}
                </label>
                <TimeSlotSelector
                  availableTimeList={
                    formData.preferredDate
                      ? availableTime[formData.preferredDate]?.availableTimes
                      : []
                  }
                  selectedPeriod={selectedPeriod}
                  onPeriodChange={setSelectedPeriod}
                  selectedTime={formData.preferredTime}
                  onTimeSelect={(timeValue) => {
                    // 使用者點擊一個時段 -> 父層更新 formData
                    setFormData((prev) => ({
                      ...prev,
                      preferredTime: timeValue,
                      pickupTime:
                        prev.needsPickup === 'yes'
                          ? calculatePickupTime(timeValue)
                          : '',
                    }));
                  }}
                />
                {selectedService && (
                  <ul className="star-list">
                    <li>
                      <a href={`tel:${phoneNumber}`}>
                        若沒有您方便的時段，請致電服務廠。{factoryLabel}：
                        <span className="link">{phoneNumber}</span>
                      </a>
                    </li>
                  </ul>
                )}
              </div>
              <div className="form-group">
                <label>
                  是否在廠等候？
                  {errors.isWaiting && (
                    <span className="error-text">{errors.isWaiting}</span>
                  )}
                </label>
                <div className="radio-group">
                  <Radio.Group
                    className="custom-radio-group"
                    value={formData.isWaiting}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        isWaiting: e.target.value,
                        needsPickup: 'no', // 取送車服務暫停，固定為 no
                        pickupAddress:
                          e.target.value === 'no' ? '' : prev.pickupAddress,
                        pickupTime:
                          e.target.value === 'no' ? '' : prev.pickupTime,
                      }));
                    }}>
                    <Flex gap="small" align="center">
                      <Radio value="yes">是</Radio>
                      <Radio value="no">否</Radio>
                    </Flex>
                  </Radio.Group>
                </div>
              </div>

              {/* 只有在場等候時才顯示是否雙人快速定保 */}
              {formData.isWaiting === 'yes' && !isQuickServiceTimeWarning && (
                <>
                  <div className="form-group">
                    <label>是否雙人快速定保服務？</label>
                    <div className="radio-group">
                      <Radio.Group
                        className="custom-radio-group"
                        value={formData.needsQuickService}
                        onChange={handleQuickServiceChange}>
                        <Flex gap="small" align="center">
                          <Radio value="yes">是</Radio>
                          <Radio value="no">否</Radio>
                        </Flex>
                      </Radio.Group>
                    </div>
                  </div>
                  {isQuickServiceTimeWarning && !isQuickServiceTimeLoading && (
                    <div className="form-group" style={{ paddingInline: 12 }}>
                      <NoticeBar
                        color="alert"
                        shape="neutral"
                        bordered
                        content={
                          '目前所選預約時段無法安排快速定保服務。若要預約快速定保，請重新選擇預約時段。'
                        }
                        icon={<ExclamationTriangleOutline />}
                      />
                    </div>
                  )}
                </>
              )}

              {/* 取送車服務暫停，先隱藏以下區塊（API 仍送 needsPickup: 'no'）
              // 只有 "不在廠等候" 時才顯示取送車服務選項
              {formData.isWaiting === 'no' && !isPickupTimeWarning && (
                <>
                  <div className="form-group">
                    <label>是否預約取送車服務？（單程距離限10公里以內）</label>
                    <div className="radio-group">
                      <Radio.Group
                        className="custom-radio-group"
                        value={formData.needsPickup}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            needsPickup: e.target.value,
                            pickupTime:
                              e.target.value === 'yes'
                                ? calculatePickupTime(prev.preferredTime)
                                : '',
                            pickupAddress:
                              e.target.value === 'yes'
                                ? prev.pickupAddress
                                : '',
                          }));
                        }}>
                        <Flex gap="small" align="center">
                          <Radio value="yes">是</Radio>
                          <Radio value="no">否</Radio>
                        </Flex>
                      </Radio.Group>
                    </div>
                  </div>
                  {isPickupTimeWarning && !isPickupTimeLoading && (
                    <div className="form-group" style={{ paddingInline: 12 }}>
                      <NoticeBar
                        color="alert"
                        shape="neutral"
                        bordered
                        content={
                          '目前所選預約時段無法安排取送車。若要預約取送車，請重新選擇預約時段。'
                        }
                        icon={<ExclamationTriangleOutline />}
                      />
                    </div>
                  )}
                </>
              )}

              // 只有選擇需要取送車服務時才顯示地址和時段
              {formData.needsPickup === 'yes' &&
                (isPickupTimeLoading ? (
                  <div className="form-group" style={{ textAlign: 'center' }}>
                    <Spin />
                  </div>
                ) : (
                  <>
                    <InputField
                      label="預約取送車地址"
                      name="pickupAddress"
                      value={formData.pickupAddress}
                      onChange={handleChange}
                      error={errors.pickupAddress}
                    />

                    <div className="form-group">
                      <label>預約取送車時段</label>
                      <input
                        type="text"
                        value={formData.pickupTime}
                        disabled
                        className="disabled-input"
                      />
                      <ul className="star-list">
                        <li>取送車時間將由專員試所需時間，另以電話通知確認</li>
                      </ul>
                    </div>
                  </>
                ))}
              */}
              <div className="form-group">
                <label>
                  電話聯絡時段
                  {errors.contactTime && (
                    <span className="error-text">{errors.contactTime}</span>
                  )}
                </label>
                {/* 預約內容（原本的電話聯絡時段） */}
                <div
                  className="picker-trigger"
                  onClick={() => setContactPickerVisible(true)}>
                  <span
                    className={`picker-placeholder ${
                      formData.contactTime ? 'has-value' : ''
                    }`}>
                    {formData.contactTime
                      ? contactOptions.find(
                          (c) => c.value === formData.contactTime
                        )?.label
                      : '請選擇'}
                  </span>
                  <DownFill className="down-icon" />
                  <CustomPicker
                    visible={contactPickerVisible}
                    onClose={() => setContactPickerVisible(false)}
                    onConfirm={(val) =>
                      setFormData((prev) => ({ ...prev, contactTime: val }))
                    }
                    title="選擇聯絡時段"
                    columns={contactOptions}
                    defaultValue={formData.contactTime}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>其他建議（限60字）</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="submit-container">
        <div className="submit-wrapper">
          <button
            onClick={handleSubmit}
            className="submit-button"
            disabled={submitDisabled}>
            確認預約
          </button>
          <ResultModal
            isVisible={isResultModalVisible}
            onClose={() => setIsResultModalVisible(false)}
            type={modalType}
            allowConfirm={true}
            isInsurance={false}
          />
        </div>
      </div>
      <ModalPopup
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        title="提示"
        content={modalMessage}
        isInsurance={false}
      />
    </div>
  );
};

export default BookingMaintenance;
